/**
 * Global ⌘K / Ctrl+K quick-switcher (mockup CommandPalette).
 *
 * Searches workspaces, canvases, artifacts, projects and objectives via
 * GET /search, then hard-navigates to the chosen result's href (results span
 * the Work / Plan / Manage shells, which are separate top-level route trees, so
 * a full navigation is the reliable jump — same pattern as signin's returnTo).
 *
 * The modal shell is CD's base-ui `CommandDialog` (overlay, Esc, focus-trap,
 * portal); the result list + keyboard nav (↑/↓ highlight, ⏎ open) are driven
 * here with an explicit `active` index rather than cmdk, which didn't reliably
 * track selection inside the dialog.
 *
 * Mounted once at the router root. Any control can open it with `openSearch()`.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { FileText, FolderKanban, LayoutGrid, Package, Search, Target } from "lucide-react";

import { useApi } from "@/api";
import { useAuth } from "@/auth";
import { CommandDialog } from "@/components/ui/command";
import { cn } from "@/lib/utils";

export const OPEN_SEARCH = "chaidojo:open-search";
/** Fire from anywhere (e.g. a sidebar search affordance) to open the palette. */
export const openSearch = () => window.dispatchEvent(new CustomEvent(OPEN_SEARCH));

interface SearchResult {
  kind: "workspace" | "canvas" | "artifact" | "project" | "objective";
  id: string;
  title: string;
  subtitle: string;
  href: string;
  mode: "work" | "plan";
}

const KIND_ICON = {
  workspace: LayoutGrid,
  canvas: FileText,
  artifact: Package,
  project: FolderKanban,
  objective: Target,
} as const;

export function CommandPalette() {
  const api = useApi();
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  // Search reaches the whole product; an account that only opens shared
  // pages has none of it (the API would refuse every query).
  const signedIn = !!auth.me && !auth.me.user.artifact_only;

  // ⌘K / Ctrl+K toggle + programmatic open event (signed-in only).
  useEffect(() => {
    if (!signedIn) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_SEARCH, onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_SEARCH, onOpen);
    };
  }, [signedIn]);

  // Fresh state each open + focus the input.
  useEffect(() => {
    if (open) {
      setQ("");
      setResults([]);
      setLoading(false);
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Debounced server search.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (!term) {
      setResults([]);
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      api
        .request<{ results: SearchResult[] }>(`/search?q=${encodeURIComponent(term)}`)
        .then((d) => {
          if (live) {
            setResults(d.results ?? []);
            setActive(0);
            setLoading(false);
          }
        })
        .catch(() => {
          if (live) {
            setResults([]);
            setLoading(false);
          }
        });
    }, 140);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q, open, api]);

  // Keep the highlighted row in view as ↑/↓ moves it.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const go = useCallback((r: SearchResult | undefined) => {
    if (!r) return;
    setOpen(false);
    window.location.assign(r.href);
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    }
    // Escape is handled by the Dialog (closes).
  };

  if (!signedIn) return null;

  const term = q.trim();

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      className="sm:max-w-xl"
      title="Search"
      description="Search workspaces, canvases, artifacts, projects and objectives"
    >
      <div className="flex items-center gap-2.5 border-b border-border px-3.5 py-3">
        <Search className="size-4 shrink-0 text-muted-foreground" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search workspaces, canvases, artifacts…"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          aria-label="Search"
          autoComplete="off"
          spellCheck={false}
        />
        <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
          esc
        </kbd>
      </div>

      <div className="max-h-[60vh] overflow-y-auto p-1.5">
        {!term ? (
          <div className="px-3 py-8 text-center text-xs text-muted-foreground">
            Type to search across everything · ↑↓ to navigate · ⏎ to open
          </div>
        ) : loading ? (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">Searching…</div>
        ) : results.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">
            No matches for “{term}”
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {results.map((r, i) => {
              const Icon = KIND_ICON[r.kind] ?? FileText;
              const isActive = i === active;
              return (
                <button
                  key={`${r.kind}-${r.id}`}
                  ref={isActive ? activeRef : undefined}
                  type="button"
                  onMouseMove={() => setActive(i)}
                  onClick={() => go(r)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors",
                    isActive ? "bg-muted" : "hover:bg-muted/60",
                  )}
                >
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground ring-1 ring-border">
                    <Icon className="size-4" />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm text-foreground">{r.title}</span>
                    <span className="truncate text-xs text-muted-foreground">{r.subtitle}</span>
                  </span>
                  <span className="shrink-0 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                    {r.mode === "plan" ? "Plan" : "Work"}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </CommandDialog>
  );
}
